<template>
  <b-container fluid>
    <b-breadcrumb :items="crumbs" />
    <b-container>
      <b-card title="Server">
        <b-form @submit.prevent="onSubmit">
          <b-form-group id="imputGroupHost" label="Host:">
            <b-form-input id="host" description="Host" v-model="server.host">
            </b-form-input>
          </b-form-group>

          <b-form-group id="inputGroupPrintersApiKey" label="Api Key:">
            <b-form-input
              id="api_key"
              description="Api Key"
              v-model="server.api_key"
            >
            </b-form-input>
          </b-form-group>

          <b-form-input
            id="server_type"
            hidden
            description="Type"
            v-model="server.server_type"
          >
          </b-form-input>

          <b-button type="submit" variant="primary">Save</b-button>
        </b-form>
      </b-card>
      <b-card v-if="!creating" title="Printers" class="mt-5">
        <b-form class="d-flex mb-3" @submit.prevent="addManualPrinter">
          <label class="sr-only" for="newPrinterDescription"
            >Printer name</label
          >
          <b-form-input
            class="mr-2"
            id="newPrinterDescription"
            placeholder="Printer name, exactly as in Windows"
            v-model="newPrinterDescription"
          ></b-form-input>
          <b-button
            type="submit"
            class="text-nowrap"
            variant="primary"
            :disabled="addingPrinter || !newPrinterDescription.trim()"
            >Add Printer</b-button
          >
        </b-form>
        <printers-list :printers="printers"></printers-list>
      </b-card>
    </b-container>
  </b-container>
</template>

<script>
import axios from "axios";
import PrintersApi from "@/printers_api";
import Notify from "@/notify";
import PrintersList from "@/components/printers/PrintersList";

export default {
  mixins: [Notify],
  components: {
    "printers-list": PrintersList,
  },
  data() {
    return {
      crumbs: [
        {
          text: "Home",
          href: "/",
        },
        {
          text: "Server",
          active: true,
        },
      ],
      server: {
        id: 0,
        api_key: "",
        host: "",
        server_type: "quickets",
      },
      printers: [],
      newPrinterDescription: "",
      addingPrinter: false,
    };
  },
  computed: {
    creating() {
      return this.server.id === 0;
    },
  },
  watch: {
    $route(to) {
      this.loadItem(to.params.id);
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.loadItem(to.params.id);
    });
  },
  methods: {
    async onSubmit() {
      if (this.creating) {
        this.create();
      } else {
        this.update();
      }
    },
    async update() {
      try {
        const response = await new PrintersApi(
          this.$auth.getAccessToken()
        ).updateServer(this.server.id, this.server);
        this.server = response.data;
        await this.syncPrinters();
        this.notifySuccess("Successfully Updated Print Server");
      } catch (e) {
        this.notifyError(e.message);
      }
    },
    async create() {
      try {
        const response = await new PrintersApi(
          this.$auth.getAccessToken()
        ).createServer(this.server);
        this.server = response.data;
        await this.syncPrinters();
        this.notifySuccess("Successfully Created Print Server");
      } catch (e) {
        this.notifyError(e.message);
      }
    },
    async syncPrinters() {
      let remotePrinters;
      try {
        remotePrinters = await this.loadQuicketPrinters();
      } catch (e) {
        this.notifyWarn(
          `Could not reach ${this.server.host} to load its printers. Syncing only works from a computer on the print server's network. Add printers manually instead.`,
          "Printers Not Synced"
        );
        return;
      }
      try {
        const localPrinters = await this.loadPrinters();
        const printerNames = localPrinters.map(
          (printer) => printer.description
        );
        const printersToCreate = remotePrinters.filter(
          (x) => printerNames.indexOf(x) === -1
        );
        await Promise.all(
          printersToCreate.map((printer) => this.addPrinter(printer))
        );
        await this.refreshPrinters();
      } catch (e) {
        this.notifyError(e.message);
      }
    },
    async addManualPrinter() {
      this.addingPrinter = true;
      try {
        await this.addPrinter(this.newPrinterDescription.trim());
        this.newPrinterDescription = "";
        await this.refreshPrinters();
        this.notifySuccess("Successfully Added Printer");
      } catch (e) {
        const errors = e.response && e.response.data;
        if (errors && errors.description) {
          this.notifyError(`Printer name ${errors.description.join(", ")}`);
        } else {
          this.notifyError(e.message);
        }
      } finally {
        this.addingPrinter = false;
      }
    },
    async addPrinter(printer) {
      await new PrintersApi(this.$auth.getAccessToken()).addPrinter(
        this.server.id,
        {
          description: printer,
        }
      );
    },
    async load(location) {
      try {
        const response = await axios.get(location);
        this.server = response.data;
        await this.refreshPrinters();
      } catch (e) {
        this.notifyError(e.message);
      }
    },
    async loadItem(id) {
      if (!Number.isInteger(parseInt(id, 0))) {
        return;
      }
      try {
        const response = await new PrintersApi(
          this.$auth.getAccessToken()
        ).retrieveServer(id);
        this.server = response.data;
        await this.refreshPrinters();
      } catch (e) {
        this.notifyError(e.message);
      }
    },
    async loadQuicketPrinters() {
      const opts = {
        headers: { "x-api-key": this.server.api_key },
        params: { api_key: this.server.api_key },
      };
      const response = await axios.get(`${this.server.host}/printers`, opts);
      return response.data;
    },
    async loadPrinters() {
      const response = await new PrintersApi(
        this.$auth.getAccessToken()
      ).retrievePrinters(this.server.id);
      return response.data;
    },
    async refreshPrinters() {
      try {
        this.printers = await this.loadPrinters();
      } catch (e) {
        this.notifyError(e);
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/printers/PrintServerCreate.vue